/**
 * Reference data for the ICD-10 sex-incompatibility gate (src/utils/icd10SexGate.js).
 *
 * WHY THIS EXISTS
 *   The note-generation AI occasionally emits a diagnosis code that is anatomically
 *   impossible for the patient's recorded sex — e.g. N95.1 "Menopausal and female
 *   climacteric states" on a MALE patient who actually has hypogonadism / low-T
 *   (the correct code is E29.1 "Testicular hypofunction"). Prompt-side guardrails
 *   reduce but do not eliminate this. This module is the deterministic, last-line
 *   gate that prevents a wrong-sex code from rendering or syncing to the EMR.
 *
 * SOURCE OF TRUTH (not hand-invented)
 *   The blocks below are the wholly-sex-specific categories of ICD-10-CM as published
 *   in the CMS IOCE "Diagnosis Edit Code Lists" (Appendix: Diagnoses for Females Only /
 *   Diagnoses for Males Only) and the Medicare Code Editor (MCE) Definitions manual.
 *   Each entry is a real ICD-10-CM block; the human-readable label is included so a
 *   clinician can verify there is nothing fabricated here.
 *
 *   IMPORTANT REGULATORY NOTE: CMS *deactivated* the MCE sex edits for Medicare claims
 *   processing on 2024-10-01 (they no longer deny a claim for a sex/dx mismatch). That
 *   makes this client-side gate MORE valuable, not less — the payer-side net that used
 *   to catch these errors is gone. We use the IOCE/MCE code lists purely as clinical
 *   sex-plausibility reference data, NOT as Medicare-edit enforcement.
 *
 * MAINTENANCE
 *   Refresh against the current-FY IOCE appendices once a year. Bump MCE_EDITION when
 *   you do. To raise precision on mixed-sex categories (e.g. D07, C79), add explicit
 *   codes to EXACT_FEMALE_ONLY / EXACT_MALE_ONLY rather than widening a block range.
 */

export const MCE_EDITION = "FY2026 (CMS IOCE/MCE sex-edit reference lists)";

/**
 * Strip behavior, switchable in one place:
 *   "silent" — incompatible codes are removed (or mirror-substituted) before they ever
 *              render or sync. Removals are still recorded for audit (see icd10SexGate).
 *   "warn"   — incompatible codes are kept but tagged `_sexConflict: true` for the UI to
 *              surface. No data is dropped.
 * Default is "silent" per product decision. Flip to "warn" to make conflicts visible
 * (e.g. for the transgender / sex-discordant edge cases CMS cited when deactivating the
 * edit) without touching the gate logic.
 */
export const GATE_MODE = "silent";

/**
 * Normalize an ICD-10-CM code for matching: uppercase, drop the dot and any whitespace.
 * "N95.1" -> "N951", " n40 " -> "N40".
 */
export function normalizeIcdForMatch(code) {
  return String(code == null ? "" : code)
    .toUpperCase()
    .replace(/[.\s]/g, "");
}

// Category ranges that are wholly FEMALE-specific by definition.
// Format: [letter, startCategoryNumber, endCategoryNumber, label]
const FEMALE_ONLY_BLOCKS = [
  ["C", 51, 58, "C51-C58 Malignant neoplasms of female genital organs"],
  ["D", 6, 6, "D06 Carcinoma in situ of cervix uteri"],
  ["D", 25, 28, "D25-D28 Benign neoplasm of uterus / ovary / other female genital"],
  ["D", 39, 39, "D39 Neoplasm of uncertain behavior of female genital organs"],
  ["E", 28, 28, "E28 Ovarian dysfunction"],
  ["N", 70, 98, "N70-N98 Disorders of female genital tract (incl. N95 menopausal disorders)"],
  ["Q", 50, 52, "Q50-Q52 Congenital malformations of female genital organs"],
  ["R", 87, 87, "R87 Abnormal findings in specimens from female genital organs"],
  ["Z", 32, 37, "Z32-Z37 Pregnancy / childbirth / delivery encounters & outcome of delivery"],
  ["Z", 39, 39, "Z39 Encounter for maternal postpartum care"],
];

// Category ranges that are wholly MALE-specific by definition.
const MALE_ONLY_BLOCKS = [
  ["C", 60, 63, "C60-C63 Malignant neoplasms of male genital organs"],
  ["D", 29, 29, "D29 Benign neoplasm of male genital organs"],
  ["D", 40, 40, "D40 Neoplasm of uncertain behavior of male genital organs"],
  ["E", 29, 29, "E29 Testicular dysfunction"],
  ["N", 40, 53, "N40-N53 Diseases of male genital organs (prostate, ED, testicular, etc.)"],
  ["Q", 53, 55, "Q53-Q55 Congenital malformations of male genital organs"],
  ["R", 86, 86, "R86 Abnormal findings in specimens from male genital organs"],
];

// Leading-prefix rules (matched on the dot-less code, so deeper subcodes are also caught):
//  - "O": ICD-10-CM Chapter 15 (O00-O9A) is Pregnancy/childbirth/puerperium — inherently
//    female; a whole-letter test also covers alphanumeric categories like O9A.
//  - "Z3A": Weeks of gestation (female).
//  - "C796": C79.6 Secondary malignant neoplasm of ovary (female).
//  - "D070".."D073": D07.0-.3 carcinoma in situ of endometrium/vulva/vagina/other female.
//    Prefix (not exact) matching so subcodes the DB carries — D07.30, D07.39 — also match.
// Verified against the indexed ICD-10-CM database. NOTE: C79.82 "Secondary malignant
// neoplasm of genital organs" is intentionally absent — it is sex-neutral (applies to
// female genital metastases too), so stripping it would be a silent false positive.
const FEMALE_ONLY_PREFIXES = ["O", "Z3A", "C796", "D070", "D071", "D072", "D073"];

// Male sub-category splits inside otherwise mixed categories. "D074".."D076" = D07.4-.6
// carcinoma in situ of penis/prostate/other male genital; prefix matching catches the DB's
// deeper subcodes (D07.60, D07.61, D07.69). Verified against the indexed ICD-10-CM database.
const MALE_ONLY_PREFIXES = ["D074", "D075", "D076"];

/**
 * Conservative, clinically-reviewed counterpart pairs. When a wrong-sex code has an
 * unambiguous same-condition code for the patient's actual sex, the gate substitutes it
 * (in "silent" mode) instead of just dropping. Keyed by normalized (dot-less) code.
 * Keep this map TINY and only add pairs that are clinically unambiguous — every entry
 * is an automated coding decision and is recorded distinctly in the audit trail.
 */
export const SEX_MIRROR_MAP = {
  // Menopausal / female climacteric states  ->  Testicular hypofunction (andropause / low-T)
  N951: { code: "E29.1", description: "Testicular hypofunction" },
};

function categoryMatchesBlocks(normCode, blocks) {
  const m = /^([A-Z])(\d{2})/.exec(normCode);
  if (!m) return false;
  const letter = m[1];
  const num = parseInt(m[2], 10);
  for (const [bl, lo, hi] of blocks) {
    if (letter === bl && num >= lo && num <= hi) return true;
  }
  return false;
}

function matchesAnyPrefix(rawCode, normCode, prefixes) {
  const upperRaw = String(rawCode == null ? "" : rawCode)
    .toUpperCase()
    .trim();
  for (const p of prefixes) {
    const np = normalizeIcdForMatch(p);
    if (normCode.startsWith(np)) return true;
    if (upperRaw.startsWith(p.toUpperCase())) return true;
  }
  return false;
}

/** True if `code` is a female-only diagnosis. */
export function isFemaleOnlyCode(code) {
  const norm = normalizeIcdForMatch(code);
  if (!norm) return false;
  if (matchesAnyPrefix(code, norm, FEMALE_ONLY_PREFIXES)) return true;
  return categoryMatchesBlocks(norm, FEMALE_ONLY_BLOCKS);
}

/** True if `code` is a male-only diagnosis. */
export function isMaleOnlyCode(code) {
  const norm = normalizeIcdForMatch(code);
  if (!norm) return false;
  if (matchesAnyPrefix(code, norm, MALE_ONLY_PREFIXES)) return true;
  return categoryMatchesBlocks(norm, MALE_ONLY_BLOCKS);
}

/**
 * Classify a single code against a normalized patient sex ("M" | "F").
 * Returns { conflict, mirror } where `mirror` is the substitution target ({code,
 * description}) when one exists for this code, else null. Never throws.
 */
export function classifyCodeForSex(code, sex) {
  const norm = normalizeIcdForMatch(code);
  if (!norm) return { conflict: false, mirror: null };
  if (sex === "M" && isFemaleOnlyCode(norm)) {
    return { conflict: true, mirror: SEX_MIRROR_MAP[norm] || null };
  }
  if (sex === "F" && isMaleOnlyCode(norm)) {
    return { conflict: true, mirror: SEX_MIRROR_MAP[norm] || null };
  }
  return { conflict: false, mirror: null };
}
